import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { posthog } from "@/lib/posthog";

const EXIT_INTENT_KEY = 'exit_intent_shown';

export const ExitIntentPopup = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [triggerMethod, setTriggerMethod] = useState('');
  
  const pageLoadTime = useRef(Date.now());
  const lastScrollY = useRef(0);
  const inactivityTimer = useRef<NodeJS.Timeout>();

  // Device detection
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Session storage helpers
  const wasShownThisSession = () => {
    return sessionStorage.getItem(EXIT_INTENT_KEY) === 'true';
  };

  const markAsShown = () => {
    sessionStorage.setItem(EXIT_INTENT_KEY, 'true');
    setHasShown(true);
  };

  // Centralized popup trigger
  const showPopup = (method: string) => {
    if (hasShown || wasShownThisSession()) return;
    
    const timeOnPage = Math.floor((Date.now() - pageLoadTime.current) / 1000);
    const scrollPercentage = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );
    
    setIsOpen(true);
    markAsShown();
    setTriggerMethod(method);
    
    // PostHog tracking
    if (posthog.__loaded) {
      posthog.capture('exit_intent_popup_shown', {
        trigger: method,
        device: isMobile ? 'mobile' : 'desktop',
        time_on_page: timeOnPage,
        scroll_depth: scrollPercentage
      });
    }

    // GTM tracking
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'exit_intent_shown',
        trigger_method: method,
        device: isMobile ? 'mobile' : 'desktop'
      });
    }

    if (import.meta.env.DEV) {
      console.log(`🎯 Exit intent triggered: ${method} (${isMobile ? 'mobile' : 'desktop'})`);
    }
  };

  // Check if already shown on mount
  useEffect(() => {
    if (wasShownThisSession()) {
      setHasShown(true);
    }
  }, []);

  // Desktop: Mouse exit intent
  useEffect(() => {
    if (isMobile || hasShown) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        showPopup('mouse_leave_top');
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShown, isMobile]);

  // Mobile: Back button intercept
  useEffect(() => {
    if (!isMobile || hasShown) return;

    // Add a history entry to intercept back button
    window.history.pushState(null, '', window.location.href);

    const handleBackButton = () => {
      showPopup('back_button');
      // Push state again to keep intercepting
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handleBackButton);
    
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [hasShown, isMobile]);

  // Mobile: Tab visibility change
  useEffect(() => {
    if (!isMobile || hasShown) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        showPopup('tab_switch');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasShown, isMobile]);

  // Mobile: Rapid scroll up detection
  useEffect(() => {
    if (!isMobile || hasShown) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollUpDistance = lastScrollY.current - currentScrollY;
      
      // Trigger if scrolled up more than 100px rapidly and near top of page
      if (scrollUpDistance > 100 && currentScrollY < 500) {
        showPopup('scroll_up');
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasShown, isMobile]);

  // Universal: Scroll depth trigger (70%)
  useEffect(() => {
    if (hasShown) return;

    const handleScroll = () => {
      const scrollPercentage = 
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100;
      
      if (scrollPercentage >= 70) {
        showPopup('scroll_70');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasShown]);

  // Universal: Inactivity detection (30 seconds)
  useEffect(() => {
    if (hasShown) return;

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        showPopup('inactivity_30s');
      }, 30000); // 30 seconds
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Start timer
    resetInactivityTimer();

    return () => {
      clearTimeout(inactivityTimer.current);
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [hasShown]);

  // Universal: Time on page fallback (45 seconds)
  useEffect(() => {
    if (hasShown) return;

    const timeoutId = setTimeout(() => {
      showPopup('time_45s');
    }, 45000); // 45 seconds

    return () => clearTimeout(timeoutId);
  }, [hasShown]);

  const handleQuizClick = () => {
    window.location.href = '/quiz';
    
    if (posthog.__loaded) {
      posthog.capture('exit_intent_popup_converted', {
        action: 'quiz_redirect',
        trigger_method: triggerMethod,
        device: isMobile ? 'mobile' : 'desktop'
      });
    }

    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'exit_intent_converted',
        conversion_type: 'quiz',
        trigger_method: triggerMethod,
        device: isMobile ? 'mobile' : 'desktop'
      });
    }

    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-clash text-2xl">
            <span className="bg-gradient-to-r from-brand-teal via-brand-purple to-brand-orange bg-clip-text text-transparent font-bold">
              Wait!
            </span>{" "}
            Discover Your Sustainability Superpower 🎯
          </DialogTitle>
          <DialogDescription className="text-base">
            {t("quiz.exitIntent.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <Button 
            onClick={handleQuizClick}
            className="w-full group" 
            size="lg"
          >
            {t("quiz.exitIntent.cta")}
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
          
          <p className="text-xs text-muted-foreground text-center">
            No spam. Just your personalised results.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
